// /careers/ hero: the team photos become the hubs of a living graph, drawn in the homepage
// hero's language (glowing marigold and white nodes on fine baltic edges, with pulses that
// travel along them). Each photo carries a fan of leaves, labeled relationships join the
// photos, and every few seconds a query traverses three hops and lights what it passes.
// With a fine pointer, hovering a photo opens its fan and shows its caption, and a dragged
// photo pulls its fan along on springs. On desktop the headline's closing period is a node too:
// an edge runs from it into the graph, and the first traversal starts there. Editors change the photos in the block editor; the
// layout below places up to seven and finds room for more.
// Without the script the photos stay a cluster of round portraits (_careers.scss).
(function () {
	'use strict';

	var hero = document.querySelector('.careers-graph');
	var stage = hero && hero.querySelector('.careers-graph__people');
	if (!stage || !window.matchMedia || !window.requestAnimationFrame || !window.Promise) { return; }

	var figures = Array.prototype.filter.call(stage.querySelectorAll('figure'), function (f) { return f.querySelector('img'); });
	var canvas = document.createElement('canvas');
	var ctx = canvas.getContext && canvas.getContext('2d');
	if (figures.length < 3 || !ctx) { return; }

	var copy = hero.querySelector('.careers-graph__copy');
	var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
	var wideQuery = window.matchMedia('(min-width: 782px)');

	// Photo positions as fractions of the free space beside the copy (desktop) or below it
	// (phones), with a size factor. The first photo is the biggest and the entrance starts there.
	var WIDE = [[0.42, 0.47, 1.36], [0.8, 0.15, 0.96], [0.12, 0.17, 0.84], [0.88, 0.63, 1.04], [0.1, 0.78, 0.8], [0.58, 0.9, 0.82], [0.46, 0.06, 0.68]];
	var NARROW = [[0.5, 0.5, 1.32], [0.84, 0.2, 0.9], [0.16, 0.2, 0.84], [0.86, 0.8, 0.94], [0.14, 0.8, 0.8], [0.5, 0.08, 0.66], [0.52, 0.93, 0.66]];
	// Relationships between photos, by position; the labeled ones read as Cypher types.
	var RELS = [[0, 1, 'KNOWS'], [2, 0, 'MENTORS'], [0, 3, 'BUILDS_WITH'], [4, 0, 'LEARNS_FROM'], [0, 5, 'SHIPS_WITH'], [3, 5, 'CHEERS_ON'], [2, 6, ''], [6, 1, ''], [1, 3, ''], [4, 5, ''], [2, 4, '']];
	var LABEL_FONT = '400 10.5px "Fira Code", ui-monospace, monospace';
	var TAU = Math.PI * 2;

	var root = window.getComputedStyle(document.documentElement);
	function color(slug, fallback) {
		var value = root.getPropertyValue('--wp--preset--color--' + slug).trim();
		var hex = (/^#[0-9a-f]{6}$/i.test(value) ? value : fallback).slice(1);
		return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
	}
	var AMBER = color('marigold-20', '#ffcf72');
	var TEAL = color('baltic-20', '#8fe3e8');
	var MIST = color('baltic-30', '#5db3bf');
	var WHITE = [255, 255, 255];

	function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
	function rgba(c, a) { return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + clamp(a, 0, 1).toFixed(3) + ')'; }
	function mix(a, b, t) { return [Math.round(a[0] + (b[0] - a[0]) * t), Math.round(a[1] + (b[1] - a[1]) * t), Math.round(a[2] + (b[2] - a[2]) * t)]; }
	function backOut(p) { var q = p - 1; return 1 + 2.70158 * q * q * q + 1.70158 * q * q; }
	// Seeded, so a resize rebuilds the same graph.
	function prng(seed) {
		return function () {
			seed = seed + 0x6D2B79F5 | 0;
			var t = Math.imul(seed ^ seed >>> 15, 1 | seed);
			t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
			return ((t ^ t >>> 14) >>> 0) / 4294967296;
		};
	}

	function sprite(c, size, stops) {
		var s = document.createElement('canvas');
		s.width = s.height = size;
		var g = s.getContext('2d'), r = size / 2, grad = g.createRadialGradient(r, r, 0, r, r, r);
		stops.forEach(function (stop) { grad.addColorStop(stop[0], stop[1] === 'core' ? 'rgba(255,255,255,1)' : rgba(c, stop[1])); });
		g.fillStyle = grad;
		g.fillRect(0, 0, size, size);
		return s;
	}
	var GLOW = [[0, 'core'], [0.14, 1], [0.36, 0.3], [1, 0]];
	var SOFT = [[0, 0.5], [0.5, 0.2], [1, 0]];
	var NODE = { amber: sprite(AMBER, 64, GLOW), white: sprite(WHITE, 64, GLOW), teal: sprite(TEAL, 64, GLOW) };
	var BOKEH = { amber: sprite(AMBER, 128, SOFT), teal: sprite(TEAL, 128, SOFT) };
	var HALO = sprite(AMBER, 128, [[0, 0.55], [0.45, 0.16], [1, 0]]);

	var card = document.createElement('div');
	card.className = 'careers-graph__card';
	card.setAttribute('aria-hidden', 'true');
	card.innerHTML = '<span class="careers-graph__card-label">(:Moment)</span><span class="careers-graph__card-text"></span>';
	var cardText = card.lastChild;
	var cardSize = { w: 0, h: 0 };

	var W = 0, H = 0, dpr = 1, wide = true, builtWidth = 0;
	var hubs = [], leaves = [], cross = [], rels = [], dust = [], bokeh = [], pulses = [], comet = null;
	var dot = null, lead = null, queries = 0;
	var pointer = null, par = { x: 0, y: 0 }, focus = null, drag = null, focusAmt = 0;
	var start = 0, last = 0, nextQuery = 0, running = false, onScreen = true, raf = 0, awake = 0;
	var labelWidths = {};

	// The copy's text lines (not its block, which spans the content width), in stage coordinates.
	function copyBox(origin) {
		if (!copy || !wide) { return null; }
		var range = document.createRange(), box = null;
		Array.prototype.forEach.call(copy.children, function (el) {
			var rects;
			if (el.matches('.wp-block-buttons')) {
				rects = [el.getBoundingClientRect()];
			} else {
				range.selectNodeContents(el);
				rects = range.getClientRects();
			}
			Array.prototype.forEach.call(rects, function (r) {
				if (!r.width) { return; }
				box = box || { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity };
				box.left = Math.min(box.left, r.left - origin.left);
				box.right = Math.max(box.right, r.right - origin.left);
				box.top = Math.min(box.top, r.top - origin.top);
				box.bottom = Math.max(box.bottom, r.bottom - origin.top);
			});
		});
		return box;
	}

	// The headline's closing period, in stage coordinates: the dot sits on the baseline.
	function period(origin) {
		var h1 = copy && copy.querySelector('h1');
		if (!h1) { return null; }
		var walker = document.createTreeWalker(h1, window.NodeFilter.SHOW_TEXT), node, lastText = null;
		while ((node = walker.nextNode())) {
			if (node.textContent.trim()) { lastText = node; }
		}
		var text = lastText ? lastText.textContent.replace(/\s+$/, '') : '';
		if (!/\.$/.test(text)) { return null; }
		var range = document.createRange();
		range.setStart(lastText, text.length - 1);
		range.setEnd(lastText, text.length);
		var r = range.getBoundingClientRect();
		return r.width ? { x: r.left - origin.left + r.width / 2, y: r.top - origin.top + r.height * 0.76, r: r.width * 0.4 } : null;
	}

	function placeDot() {
		var p = dot && period(stage.getBoundingClientRect());
		if (p) {
			dot.x = p.x;
			dot.y = p.y;
		}
	}

	function inside(box, x, y, pad) {
		return box && x > box.left - pad && x < box.right + pad && y > box.top - pad && y < box.bottom + pad;
	}

	function build() {
		var origin = stage.getBoundingClientRect();
		wide = wideQuery.matches;
		W = origin.width;
		H = origin.height;
		if (!W || !H) { return false; }
		builtWidth = window.innerWidth;
		dpr = Math.min(window.devicePixelRatio || 1, 2);
		canvas.width = Math.round(W * dpr);
		canvas.height = Math.round(H * dpr);

		var box = copyBox(origin);
		var area;
		if (box) {
			var right = W - Math.max(32, (W - 1152) / 2 * 0.6);
			var left = Math.min(Math.max(box.right + 64, W * 0.46), right - 280);
			area = { x: left, y: H * 0.1, w: right - left, h: H * 0.8 };
		} else {
			area = { x: 20, y: 18, w: W - 40, h: H - 36 };
		}
		var layout = wide ? WIDE : NARROW;
		var base = wide ? clamp(Math.min(area.w * 0.21, area.h * 0.18), 72, 128) : clamp(area.w * 0.2, 54, 84);
		var rand = prng(7);

		hubs = [];
		figures.forEach(function (fig, i) {
			var a = layout[i] || roomFor(area, rand);
			var size = Math.round(base * a[2]);
			var x = area.x + a[0] * area.w, y = area.y + a[1] * area.h;
			fig.style.width = fig.style.height = size + 'px';
			hubs.push({ isHub: true, el: fig, label: fig.querySelector('img').alt, r: size / 2, f: a[2], ax: x, ay: y, x: x, y: y, vx: 0, vy: 0, phase: rand() * TAU, born: 0, scale: reduced ? 1 : 0, flash: 0, hot: 0, spokes: [], tr: '', op: '' });
		});
		// The entrance ripples out from the biggest photo.
		hubs.slice().sort(function (a, b) {
			return Math.hypot(a.ax - hubs[0].ax, a.ay - hubs[0].ay) - Math.hypot(b.ax - hubs[0].ax, b.ay - hubs[0].ay);
		}).forEach(function (h, order) { h.born = 150 + order * 110; });

		leaves = [];
		hubs.forEach(function (h) {
			var count = Math.round((wide ? 9 : 6) + (wide ? 11 : 5) * h.f);
			for (var k = 0; k < count; k++) {
				var ang = rand() * TAU;
				var reach = h.r + 8 + Math.pow(rand(), 1.3) * (h.r * 0.85 + (wide ? 34 : 18));
				var leaf = addLeaf(h, h, Math.cos(ang) * reach, Math.sin(ang) * reach, 1.1 + rand() * 2.1, rand, box);
				leaf.born = h.born + 300 + (reach - h.r) * 3 + rand() * 120;
				if (rand() < 0.42) {
					var ang2 = ang + (rand() - 0.5) * 1.1, reach2 = 16 + rand() * (wide ? 30 : 16);
					addLeaf(h, leaf, leaf.ox + Math.cos(ang2) * reach2, leaf.oy + Math.sin(ang2) * reach2, 0.8 + rand() * 1.3, rand, box).born = leaf.born + 160;
				}
			}
		});

		// A weave between neighboring fans, kept off the copy.
		cross = [];
		var reachX = wide ? 64 : 40, maxX = wide ? 46 : 18;
		for (var i = 0; i < leaves.length && cross.length < maxX; i++) {
			for (var j = i + 1; j < leaves.length && cross.length < maxX; j++) {
				var p = leaves[i], q = leaves[j];
				if (p.hub !== q.hub && p.dim === 1 && q.dim === 1 && Math.hypot(p.x - q.x, p.y - q.y) < reachX && rand() < 0.55) {
					cross.push({ from: p, to: q });
				}
			}
		}

		rels = [];
		RELS.forEach(function (r) {
			if (hubs[r[0]] && hubs[r[1]]) { rels.push({ from: hubs[r[0]], to: hubs[r[1]], label: r[2] ? ':' + r[2] : '', glow: 0, born: 0 }); }
		});
		hubs.forEach(function (h) {
			if (rels.some(function (r) { return r.from === h || r.to === h; })) { return; }
			var near = hubs.filter(function (o) { return o !== h; }).sort(function (a, b) { return Math.hypot(a.ax - h.ax, a.ay - h.ay) - Math.hypot(b.ax - h.ax, b.ay - h.ay); })[0];
			rels.push({ from: near, to: h, label: '', glow: 0, born: 0 });
		});
		rels.forEach(function (r) { r.born = Math.max(r.from.born, r.to.born) + 560; });

		var p = box && period(origin);
		dot = p ? { isHub: true, x: p.x, y: p.y, r: p.r, scale: 1, hot: 0, flash: 0 } : null;
		lead = dot ? { from: dot, to: hubs[0], label: '', glow: 0, born: hubs[0].born + 700 } : null;

		dust = [];
		for (var d = 0, nd = wide ? 44 : 20; d < nd; d++) {
			var spread = rand() < 0.7;
			var dx = spread ? area.x - 60 + rand() * (area.w + 120) : rand() * W, dy = spread ? area.y - 40 + rand() * (area.h + 80) : rand() * H;
			dust.push({ x: dx, y: dy, s: 0.4 + rand() * 0.7, a: 0.12 + rand() * 0.3, tw: rand() * TAU, dim: inside(box, dx, dy, 16) ? 0.2 : 1 });
		}
		bokeh = [];
		for (var b = 0, nb = wide ? 11 : 6; b < nb; b++) {
			bokeh.push({ x: area.x - 40 + rand() * (area.w + 80), y: area.y - 30 + rand() * (area.h + 60), r: wide ? 30 + rand() * 80 : 20 + rand() * 40, tint: rand() < 0.55 ? 'amber' : 'teal', a: 0.05 + rand() * 0.09, ph: rand() * TAU, sp: 0.00012 + rand() * 0.00018 });
		}
		pulses = [];
		comet = null;
		return true;
	}

	// For photos past the layout: the spot farthest from the others.
	function roomFor(area, rand) {
		var best = null, bestD = -1;
		for (var n = 0; n < 40; n++) {
			var c = [0.08 + rand() * 0.84, 0.08 + rand() * 0.84];
			var dmin = Math.min.apply(null, hubs.map(function (h) { return Math.hypot(area.x + c[0] * area.w - h.ax, area.y + c[1] * area.h - h.ay) - h.r; }));
			if (dmin > bestD) { bestD = dmin; best = c; }
		}
		return [best[0], best[1], 0.64];
	}

	function addLeaf(h, parent, ox, oy, size, rand, box) {
		var t = rand();
		var leaf = { hub: h, ox: ox, oy: oy, x: h.ax + ox, y: h.ay + oy, vx: 0, vy: 0, size: size, tint: t < 0.62 ? 'amber' : t < 0.86 ? 'white' : 'teal', tw: rand() * TAU, born: 0 };
		leaf.dim = inside(box, leaf.x, leaf.y, 24) ? 0.14 : 1;
		leaves.push(leaf);
		h.spokes.push({ from: parent, to: leaf });
		return leaf;
	}

	function intro(born, dur, el) { return reduced ? 1 : clamp((el - born) / dur, 0, 1); }

	function spring(n, tx, ty, stiff, damp, k) {
		if (reduced) {
			n.x = tx;
			n.y = ty;
			return;
		}
		var keep = Math.pow(damp, k);
		n.vx = (n.vx + (tx - n.x) * stiff * k) * keep;
		n.vy = (n.vy + (ty - n.y) * stiff * k) * keep;
		n.x += n.vx * k;
		n.y += n.vy * k;
	}

	// Where an edge starts and ends: at the rim of a photo rather than its center.
	function ends(from, to, padFrom, padTo) {
		var dx = to.x - from.x, dy = to.y - from.y, d = Math.sqrt(dx * dx + dy * dy) || 1, ux = dx / d, uy = dy / d;
		var s = from.isHub ? from.r * from.scale + padFrom : 0, e = to.isHub ? to.r * to.scale + padTo : 0;
		return { sx: from.x + ux * s, sy: from.y + uy * s, ex: to.x - ux * e, ey: to.y - uy * e, ux: ux, uy: uy, len: d - s - e };
	}

	function addPulse(edge, dir, speed, hot) {
		pulses.push({ e: edge, dir: dir, t: 0, s: speed, hot: hot });
	}

	function burst(h) {
		h.flash = 1;
		if (!h.spokes) { return; }
		h.spokes.filter(function (s) { return s.from === h; }).sort(function () { return Math.random() - 0.5; }).slice(0, 9).forEach(function (s) {
			addPulse(s, 1, 0.02 + Math.random() * 0.012, true);
		});
	}

	// MATCH (a)-[*3]-(b): three hops from the hovered photo, or from any. The first starts at the
	// headline's period, and now and then another does.
	function query(t) {
		var fromDot = lead && !focus && (queries === 0 || Math.random() < 0.25);
		var cur = fromDot ? lead.to : focus || hubs[Math.floor(Math.random() * hubs.length)], seen = [cur], path = [];
		queries++;
		if (fromDot) { path.push({ rel: lead, from: dot, to: cur }); }
		for (var s = path.length; s < 3; s++) {
			var options = rels.filter(function (r) {
				var other = r.from === cur ? r.to : r.to === cur ? r.from : null;
				return other && seen.indexOf(other) < 0;
			});
			if (!options.length) { break; }
			var rel = options[Math.floor(Math.random() * options.length)], next = rel.from === cur ? rel.to : rel.from;
			path.push({ rel: rel, from: cur, to: next });
			seen.push(next);
			cur = next;
		}
		if (!path.length) {
			nextQuery = t + 3600;
			return;
		}
		burst(path[0].from);
		comet = { path: path, i: 0, t: 0, trail: [] };
	}

	function step(t, k) {
		var el = t - start;
		var live = pointer && fine && !reduced;
		par.x += ((live ? (pointer.x - W * 0.7) / W * -18 : 0) - par.x) * Math.min(1, 0.05 * k);
		par.y += ((live ? (pointer.y - H / 2) / H * -12 : 0) - par.y) * Math.min(1, 0.05 * k);
		focusAmt += ((focus ? 1 : 0) - focusAmt) * (reduced ? 1 : Math.min(1, 0.14 * k));

		hubs.forEach(function (h) {
			var tx = h.ax, ty = h.ay;
			if (h === drag && pointer) {
				tx = pointer.x;
				ty = pointer.y;
			} else if (!reduced) {
				tx += Math.sin(el * 0.00041 + h.phase) * 7 + par.x * h.f;
				ty += Math.cos(el * 0.00033 + h.phase * 1.7) * 9 + par.y * h.f;
			}
			spring(h, tx, ty, 0.07, 0.8, k);
			h.scale = reduced ? 1 : backOut(intro(h.born, 650, el));
			h.flash = Math.max(0, h.flash - 0.018 * k);
			h.hot += ((focus === h ? 1 : 0) - h.hot) * (reduced ? 1 : Math.min(1, 0.16 * k));
		});

		leaves.forEach(function (l) {
			var h = l.hub, open = 1 + h.hot * 0.12;
			var tx = h.x + l.ox * open, ty = h.y + l.oy * open;
			// The pointer parts the fans as it passes.
			if (live && !drag) {
				var dx = tx - pointer.x, dy = ty - pointer.y, d = Math.sqrt(dx * dx + dy * dy);
				if (d < 120 && d > 0.1) {
					var push = Math.pow(1 - d / 120, 2) * 30;
					tx += dx / d * push;
					ty += dy / d * push;
				}
			}
			spring(l, tx, ty, 0.05, 0.84, k);
		});

		if (reduced) { return; }

		if (Math.random() < 0.16 * k) {
			var h0 = hubs[Math.floor(Math.random() * hubs.length)], s0 = h0.spokes[Math.floor(Math.random() * h0.spokes.length)];
			if (s0 && el > s0.to.born + 200) { addPulse(s0, 1, 0.012 + Math.random() * 0.012, false); }
		}
		if (Math.random() < 0.025 * k) {
			var r0 = lead && Math.random() < 0.12 ? lead : rels[Math.floor(Math.random() * rels.length)];
			if (r0 && el > r0.born + 900) { addPulse(r0, Math.random() < 0.5 ? 1 : -1, 0.008 + Math.random() * 0.006, false); }
		}
		pulses.forEach(function (p) { p.t += p.s * k; });
		pulses = pulses.filter(function (p) { return p.t < 1; });
		rels.forEach(function (r) { r.glow = Math.max(0, r.glow - 0.012 * k); });
		if (lead) {
			lead.glow = Math.max(0, lead.glow - 0.012 * k);
			dot.flash = Math.max(0, dot.flash - 0.018 * k);
		}

		if (comet) {
			var hop = comet.path[comet.i];
			hop.rel.glow = 1;
			comet.t += 0.02 * k;
			if (comet.t >= 1) {
				burst(hop.to);
				comet.i++;
				comet.t = 0;
				comet.trail = [];
				if (comet.i >= comet.path.length) {
					comet = null;
					nextQuery = t + 3400;
				}
			}
		} else if (t >= nextQuery && !drag) {
			query(t);
		}
	}

	function render(t) {
		var el = t - start;
		var fade = intro(0, 1200, el);
		ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		ctx.clearRect(0, 0, W, H);

		// Depth: out-of-focus light and a field of faint stars.
		ctx.globalCompositeOperation = 'lighter';
		bokeh.forEach(function (b) {
			var x = b.x + (reduced ? 0 : Math.sin(el * b.sp + b.ph) * 18), y = b.y + (reduced ? 0 : Math.cos(el * b.sp * 0.8 + b.ph) * 12);
			ctx.globalAlpha = b.a * fade;
			ctx.drawImage(BOKEH[b.tint], x - b.r, y - b.r, b.r * 2, b.r * 2);
		});
		dust.forEach(function (d) {
			ctx.globalAlpha = d.a * (reduced ? 0.8 : 0.55 + 0.45 * Math.sin(el * 0.0012 + d.tw)) * d.dim * fade;
			ctx.drawImage(NODE.white, d.x - d.s * 3, d.y - d.s * 3, d.s * 6, d.s * 6);
		});
		ctx.globalAlpha = 1;
		ctx.globalCompositeOperation = 'source-over';

		ctx.lineWidth = 0.6;
		ctx.strokeStyle = rgba(MIST, 0.16 * (1 - focusAmt * 0.6) * intro(1500, 700, el));
		ctx.beginPath();
		cross.forEach(function (c) {
			ctx.moveTo(c.from.x, c.from.y);
			ctx.lineTo(c.to.x, c.to.y);
		});
		ctx.stroke();

		ctx.lineWidth = 0.8;
		hubs.forEach(function (h) {
			var a = 0.34 * (1 + h.hot * 0.9) * (focus && focus !== h ? 1 - focusAmt * 0.65 : 1);
			[1, 0.14].forEach(function (dim) {
				ctx.strokeStyle = rgba(MIST, a * dim);
				ctx.beginPath();
				h.spokes.forEach(function (s) {
					if (s.to.dim !== dim) { return; }
					var g = intro(s.to.born - 380, 380, el);
					if (!g) { return; }
					var e = ends(s.from, s.to, 3, 0);
					if (e.len <= 0) { return; }
					ctx.moveTo(e.sx, e.sy);
					ctx.lineTo(e.sx + (e.ex - e.sx) * g, e.sy + (e.ey - e.sy) * g);
				});
				ctx.stroke();
			});
		});

		// Relationships between photos: an arrow and, on most, a type.
		ctx.font = LABEL_FONT;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		(lead ? rels.concat(lead) : rels).forEach(function (r) {
			var g = intro(r.born, 700, el);
			if (!g) { return; }
			var e = ends(r.from, r.to, 5, 6);
			if (e.len < 16) { return; }
			var lit = Math.max(r.glow, r.from.hot, r.to.hot);
			var dimR = focus && focus !== r.from && focus !== r.to ? 1 - focusAmt * 0.7 : 1;
			var tone = rgba(mix(TEAL, AMBER, r.glow), (0.58 + lit * 0.42) * dimR);
			var bx = e.ex - e.ux * 7, by = e.ey - e.uy * 7;
			if (labelWidths[r.label] === undefined) { labelWidths[r.label] = r.label ? ctx.measureText(r.label).width + 14 : 0; }
			var gap = g === 1 && e.len > labelWidths[r.label] + 30 ? labelWidths[r.label] : 0;
			var mx = (e.sx + bx) / 2, my = (e.sy + by) / 2;
			ctx.strokeStyle = tone;
			ctx.lineWidth = 1.3 + lit * 0.7;
			ctx.beginPath();
			if (gap) {
				ctx.moveTo(e.sx, e.sy);
				ctx.lineTo(mx - e.ux * gap / 2, my - e.uy * gap / 2);
				ctx.moveTo(mx + e.ux * gap / 2, my + e.uy * gap / 2);
				ctx.lineTo(bx, by);
			} else {
				ctx.moveTo(e.sx, e.sy);
				ctx.lineTo(e.sx + (bx - e.sx) * g, e.sy + (by - e.sy) * g);
			}
			ctx.stroke();
			if (g < 1) { return; }
			ctx.fillStyle = tone;
			ctx.beginPath();
			ctx.moveTo(e.ex, e.ey);
			ctx.lineTo(bx - e.uy * 3.5, by + e.ux * 3.5);
			ctx.lineTo(bx + e.uy * 3.5, by - e.ux * 3.5);
			ctx.closePath();
			ctx.fill();
			if (gap) {
				var rot = Math.atan2(e.uy, e.ux);
				if (rot > Math.PI / 2 || rot < -Math.PI / 2) { rot += Math.PI; }
				ctx.save();
				ctx.translate(mx, my);
				ctx.rotate(rot);
				ctx.fillStyle = rgba(mix(TEAL, AMBER, Math.max(r.glow, lit * 0.6)), (0.85 + lit * 0.15) * dimR * intro(r.born + 700, 400, el));
				ctx.fillText(r.label, 0, 0.5);
				ctx.restore();
			}
		});

		ctx.globalCompositeOperation = 'lighter';
		leaves.forEach(function (l) {
			var g = intro(l.born, 260, el);
			if (!g) { return; }
			var h = l.hub, emph = focus ? (focus === h ? 1.35 : 1 - focusAmt * 0.6) : 1;
			var s = l.size * (g < 1 ? backOut(g) : 1) * (1 + h.hot * 0.3) * 4;
			ctx.globalAlpha = clamp((reduced ? 0.9 : 0.78 + 0.22 * Math.sin(el * 0.0021 + l.tw)) * l.dim * emph * (0.85 + h.flash * 0.6), 0, 1);
			ctx.drawImage(NODE[l.tint], l.x - s, l.y - s, s * 2, s * 2);
		});
		hubs.forEach(function (h) {
			if (h.scale <= 0) { return; }
			var s = h.r * h.scale * 1.9 * (1 + h.flash * 0.25);
			ctx.globalAlpha = clamp((0.3 + h.hot * 0.25 + h.flash * 0.5) * (focus && focus !== h ? 1 - focusAmt * 0.5 : 1), 0, 1);
			ctx.drawImage(HALO, h.x - s, h.y - s, s * 2, s * 2);
		});
		if (dot) {
			var ds = (9 + dot.flash * 10) * intro(lead.born - 300, 500, el);
			ctx.globalAlpha = clamp((reduced ? 0.7 : 0.55 + 0.25 * Math.sin(el * 0.003)) + dot.flash, 0, 1);
			ctx.drawImage(NODE.amber, dot.x - ds, dot.y - ds, ds * 2, ds * 2);
		}
		pulses.forEach(function (p) {
			var e = ends(p.e.from, p.e.to, 3, 6), u = p.dir > 0 ? p.t : 1 - p.t, s = p.hot ? 9 : 6;
			ctx.globalAlpha = Math.sin(Math.PI * p.t) * (p.hot ? 1 : 0.8);
			ctx.drawImage(p.hot ? NODE.white : NODE.amber, e.sx + (e.ex - e.sx) * u - s, e.sy + (e.ey - e.sy) * u - s, s * 2, s * 2);
		});
		if (comet) {
			var hop = comet.path[comet.i], ce = ends(hop.from, hop.to, 5, 6);
			for (var n = 0; n < 6; n++) {
				var ct = comet.t - n * 0.035;
				if (ct < 0) { break; }
				var cs = 13 - n * 1.6;
				ctx.globalAlpha = 1 - n * 0.15;
				ctx.drawImage(n ? NODE.amber : NODE.white, ce.sx + (ce.ex - ce.sx) * ct - cs, ce.sy + (ce.ey - ce.sy) * ct - cs, cs * 2, cs * 2);
			}
		}
		ctx.globalAlpha = 1;
		ctx.globalCompositeOperation = 'source-over';

		hubs.forEach(function (h) {
			var dimH = focus && focus !== h ? 1 - focusAmt * 0.5 : 1, grow = 1 + h.hot * 0.06, r = h.r * h.scale * grow;
			if (h.scale > 0) {
				ctx.lineWidth = 1.5;
				ctx.strokeStyle = rgba(TEAL, (0.55 + h.hot * 0.45) * dimH);
				ctx.beginPath();
				ctx.arc(h.x, h.y, r + 3, 0, TAU);
				ctx.stroke();
				if (h.hot > 0.02) {
					ctx.lineWidth = 2;
					ctx.strokeStyle = rgba(AMBER, h.hot * 0.95);
					ctx.beginPath();
					ctx.arc(h.x, h.y, r + 9 + (1 - h.hot) * 6, 0, TAU);
					ctx.stroke();
				}
				if (h.flash > 0.01) {
					ctx.lineWidth = 1.5;
					ctx.strokeStyle = rgba(AMBER, h.flash * 0.9);
					ctx.beginPath();
					ctx.arc(h.x, h.y, r + 4 + (1 - h.flash) * 34, 0, TAU);
					ctx.stroke();
				}
			}
			var tr = 'translate3d(' + (h.x - h.r).toFixed(1) + 'px,' + (h.y - h.r).toFixed(1) + 'px,0) scale(' + Math.max(0, h.scale * grow).toFixed(3) + ')';
			if (tr !== h.tr) {
				h.el.style.transform = tr;
				h.tr = tr;
			}
			var op = dimH.toFixed(2);
			if (op !== h.op) {
				h.el.style.opacity = op;
				h.op = op;
			}
		});

		if (focus) {
			var x = focus.x + focus.r + 22;
			if (x + cardSize.w > W - 12) { x = focus.x - focus.r - 22 - cardSize.w; }
			card.style.transform = 'translate3d(' + Math.round(x) + 'px,' + Math.round(clamp(focus.y - cardSize.h / 2, 8, H - cardSize.h - 8)) + 'px,0)';
		}
	}

	function frame(t) {
		raf = 0;
		var k = last ? Math.min(3, (t - last) / 16.667) : 1;
		last = t;
		step(t, k);
		render(t);
		if (running && onScreen && !document.hidden && (!reduced || t < awake)) {
			raf = window.requestAnimationFrame(frame);
		} else {
			last = 0;
		}
	}

	function wake() {
		awake = window.performance.now() + 500;
		if (!raf && running && onScreen && !document.hidden) { raf = window.requestAnimationFrame(frame); }
	}

	function setFocus(h) {
		if (h === focus) { return; }
		focus = h;
		stage.classList.toggle('is-over', !!h);
		card.classList.toggle('is-shown', !!h);
		if (h) {
			cardText.textContent = h.label;
			cardSize = { w: card.offsetWidth, h: card.offsetHeight };
			if (!comet) { nextQuery = Math.min(nextQuery, window.performance.now() + 350); }
		}
	}

	function local(e) {
		var r = stage.getBoundingClientRect();
		return { x: e.clientX - r.left, y: e.clientY - r.top };
	}

	function hit(p) {
		var found = null;
		hubs.forEach(function (h) {
			if (h.scale > 0.5 && Math.hypot(p.x - h.x, p.y - h.y) <= h.r + 6) { found = h; }
		});
		return found;
	}

	function onControl(e) {
		return e.target && e.target.closest && e.target.closest('a, button');
	}

	if (fine) {
		hero.addEventListener('pointermove', function (e) {
			pointer = local(e);
			if (!drag) { setFocus(onControl(e) ? null : hit(pointer)); }
			wake();
		});
		hero.addEventListener('pointerleave', function () {
			pointer = null;
			if (!drag) { setFocus(null); }
			wake();
		});
		hero.addEventListener('pointerdown', function (e) {
			if (e.button !== 0 || !focus || onControl(e)) { return; }
			drag = focus;
			pointer = local(e);
			try { hero.setPointerCapture(e.pointerId); } catch (err) { /* capture is a nicety */ }
			stage.classList.add('is-dragging');
			card.classList.remove('is-shown');
			e.preventDefault();
			wake();
		});
		var release = function () {
			if (!drag) { return; }
			var over = pointer ? hit(pointer) : null;
			drag = null;
			stage.classList.remove('is-dragging');
			if (over === focus) {
				card.classList.toggle('is-shown', !!over);
			} else {
				setFocus(over);
			}
			wake();
		};
		hero.addEventListener('pointerup', release);
		hero.addEventListener('pointercancel', release);
	}

	hero.classList.add('is-graph');
	canvas.className = 'careers-graph__canvas';
	canvas.setAttribute('aria-hidden', 'true');
	stage.insertBefore(canvas, stage.firstChild);
	stage.appendChild(card);
	figures.forEach(function (f) {
		var img = f.querySelector('img');
		f.classList.add('careers-graph__node');
		f.style.transform = 'scale(0)';
		img.draggable = false;
		if (img.loading === 'lazy') { img.loading = 'eager'; }
	});

	// The accent line arrives with a small rise; follow its period once it lands.
	var accent = copy && copy.querySelector('h1 mark');
	if (accent) { accent.addEventListener('animationend', placeDot); }

	var images = Promise.all(figures.map(function (f) {
		var img = f.querySelector('img');
		return img.decode ? img.decode().catch(function () {}) : null;
	}));
	var font = document.fonts && document.fonts.load ? document.fonts.load(LABEL_FONT).catch(function () {}) : null;
	Promise.race([Promise.all([images, font]), new Promise(function (resolve) { window.setTimeout(resolve, 1500); })]).then(function () {
		if (!build()) { return; }
		start = window.performance.now();
		nextQuery = start + 2300;
		running = true;
		wake();
	});

	var resizeTimer = 0;
	window.addEventListener('resize', function () {
		window.clearTimeout(resizeTimer);
		resizeTimer = window.setTimeout(function () {
			// Phones fire resize as the address bar moves; only a width change needs a new layout.
			if (!running || window.innerWidth === builtWidth) { return; }
			focus = drag = null;
			card.classList.remove('is-shown');
			build();
			start = window.performance.now() - 100000;
			wake();
		}, 160);
	});

	if ('IntersectionObserver' in window) {
		new IntersectionObserver(function (entries) {
			onScreen = entries[0].isIntersecting;
			if (onScreen) { wake(); }
		}).observe(hero);
	}
	document.addEventListener('visibilitychange', function () {
		if (!document.hidden) { wake(); }
	});
}());
